import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const DEFAULT_ENTRY = { open: "", close: "", closed: false };

export default function HoursEditor({ value, onChange }) {
  const hours = value || {};

  const updateDay = (day, patch) => {
    const next = {
      ...hours,
      [day]: { ...DEFAULT_ENTRY, ...(hours[day] || {}), ...patch },
    };
    onChange(next);
  };

  const copyMondayToWeekdays = () => {
    const mon = hours.mon;
    if (!mon || mon.closed) return;
    const next = { ...hours };
    ["tue", "wed", "thu", "fri"].forEach((d) => {
      next[d] = { ...mon };
    });
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="hours-editor">
      <p className="text-sm text-gray-600">
        Set the times your business is open each day. Use 24-hour format (e.g. 09:00, 17:30). Toggle Closed for days you're shut.
      </p>
      <div className="rounded-md border border-border divide-y">
        {DAYS.map(({ key, label }) => {
          const entry = hours[key] || DEFAULT_ENTRY;
          const closed = !!entry.closed;
          return (
            <div
              key={key}
              className="grid grid-cols-[110px_1fr] sm:grid-cols-[140px_1fr_1fr_140px] items-center gap-3 px-3 py-3"
              data-testid={`hours-row-${key}`}
            >
              <div className="font-medium text-sm text-gray-900">{label}</div>
              <div>
                <Label htmlFor={`hours-${key}-open`} className="sr-only">Open time</Label>
                <Input
                  id={`hours-${key}-open`}
                  type="time"
                  value={entry.open || ""}
                  disabled={closed}
                  onChange={(e) => updateDay(key, { open: e.target.value, closed: false })}
                  className="rounded-md h-9"
                  data-testid={`hours-${key}-open`}
                />
              </div>
              <div>
                <Label htmlFor={`hours-${key}-close`} className="sr-only">Close time</Label>
                <Input
                  id={`hours-${key}-close`}
                  type="time"
                  value={entry.close || ""}
                  disabled={closed}
                  onChange={(e) => updateDay(key, { close: e.target.value, closed: false })}
                  className="rounded-md h-9"
                  data-testid={`hours-${key}-close`}
                />
              </div>
              <div className="flex items-center gap-2 justify-end col-span-2 sm:col-span-1">
                <span className="text-xs font-mono uppercase tracking-wider text-gray-500">Closed</span>
                <Switch
                  checked={closed}
                  onCheckedChange={(v) => updateDay(key, { closed: !!v, open: "", close: "" })}
                  data-testid={`hours-${key}-closed`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={copyMondayToWeekdays}
        className="text-xs font-mono text-[#B31E1E] hover:underline"
        data-testid="hours-copy-mon-to-fri"
      >
        Copy Monday → Tue–Fri
      </button>
    </div>
  );
}
